import { formatValue, humanizeMeasure, formatFromColumn } from "@/lib/reports/labels";

import type { ChartBuildCtx, ChartOption } from "./context";
import { aggregateByCategory } from "./helpers";

export function buildTreemapOption({ data, base, xKey, yKey }: ChartBuildCtx): ChartOption {
  // Same rollup as bar charts — collapse rows that share a category name.
  const aggregated = aggregateByCategory(data.rows, xKey, [yKey], data.columns, false);
  aggregated.sort((a, b) => Number(b[yKey] ?? 0) - Number(a[yKey] ?? 0));
  const items = aggregated.map((r, i) => ({
    name: String(r[xKey] ?? `item-${i}`),
    value: Number(r[yKey] ?? 0),
  }));
  const yFmt = formatFromColumn(yKey, data.columns);
  return {
    ...base,
    tooltip: {
      trigger: "item",
      formatter: (p: { name: string; value: number }) =>
        `<b>${p.name}</b><br/>${humanizeMeasure(yKey)}: ${formatValue(p.value, yFmt)}`,
    },
    series: [
      {
        type: "treemap",
        data: items,
        roam: false,
        nodeClick: false,
        // Force tiles to fill the entire chart area. Default ECharts
        // squarify can leave large empty regions when tile values are
        // similar (rendering as a single row of squares in the corner).
        // Setting width/height to 100% with left/top=0 plus a wider
        // squareRatio biases toward rectangular tiles that match the
        // canvas aspect, so the treemap always uses the full canvas.
        width: "100%",
        height: "100%",
        left: 0,
        top: 0,
        right: 0,
        bottom: 0,
        squareRatio: 1.6,
        label: {
          show: true,
          // Hide labels on small tiles — ECharts already supports
          // overflow truncation but tiles smaller than ~64x36 px
          // can't fit two lines legibly. lineHeight + ellipsis keep
          // the rest tidy.
          fontSize: 12,
          fontWeight: 600,
          color: "#fff",
          overflow: "truncate",
          textBorderColor: "rgba(17,24,39,0.45)",
          textBorderWidth: 2,
          minMargin: 4,
          formatter: (p: { name: string; value: number }) => {
            const v = formatValue(p.value, yFmt);
            const name = p.name.length > 22 ? p.name.slice(0, 21) + "…" : p.name;
            return `${name}\n${v}`;
          },
        },
        // Tooltip carries the full untruncated name.
        upperLabel: { show: false },
        breadcrumb: { show: false },
        itemStyle: { borderColor: "#fff", borderWidth: 2, gapWidth: 2 },
        levels: [
          {
            colorSaturation: [0.35, 0.7],
            itemStyle: { borderWidth: 0, gapWidth: 2 },
          },
        ],
      },
    ],
  } as ChartOption;
}
